import { SuccessfulAddContractDialog } from '../components/contracts/successfulAddContractDialog';
import { AddContractDialoge } from '../components/contracts/addContractDialog';
import ContractsPageHeader from '../components/contracts/contractsPageHeader';
import ContractsFiltration from '../components/contracts/contractFiltration';
import ContractsListView from '../components/contracts/contractsListView';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Contracts = () => {

    const hasContractCreatePermission = useHasPermission('contract_create');

    const [contractId, setContractId] = useState(null);
    const [openAddContract, setOpenAddContract] = useState(false);
    const [openSuccessfulAddContract, setOpenSuccessfulAddContract] = useState(false);

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contracts, setContracts] = useState([]);
    const [totalContracts, setTotalContracts] = useState('');
    const [contractsFetched, setContractsFetched] = useState(0);
    const [filteredContractsCount, setFilteredContractsCount] = useState(0);
    const [contractFilters, setContractfilters] = useState(
        JSON.parse(localStorage.getItem('contractFilters')) ||
        { debouncedSearch: '', company: '', auto_renew: null, date: null }
    );
    const defaultSortBy = {
        title: null,
        contract_number: null,
        company: null,
        date: null,
        visit_frequency: null,
    };
    
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('contractSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'contracts',
        pathname: '/dashboard/contract',
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: contractFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(contractFilters);

    useEffect(() => {
        localStorage.setItem('contractFilters', JSON.stringify(contractFilters));
    }, [contractFilters]);

    useEffect(() => {
        localStorage.setItem('contractSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        setLoading(true)
    }, [contractFilters]);

    const fetchContracts = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('contract', { params: { offset: adjustedOffset, limit, filters: contractFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedContracts = [];
                // Update the fetched records in their correct positions
                response?.data?.contractsData?.forEach((file, index) => { updatedContracts[adjustedOffset + index] = file });
                // Update the state with the new files array
                setLoading(false)
                setContracts(updatedContracts);
                setFilteredContractsCount(response?.data?.filteredContractsCount);
                setTotalContracts(response?.data?.totalContractsCount);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching contracts:', error);
            CustomErrorLogger(error, 'fetch contracts in contracts page');
        }
    }, [offset, contractFilters, sortBy]);

    const fetchContractsFiltrationArrays = useCallback(async () => {
        try {
            const response = await axios.get('contract/getContractFiltrationArrays', { params: { contractFilters } });
            if (response.status === 200 || response.status === 201) {
                setCompanies(response?.data?.companyArray);
            }
        } catch (error) {
            console.error('Error Fetching contracts filtration arrays:', error);
            CustomErrorLogger(error, 'fetch contracts filtration arrays in contracts page');
        }
    }, [contractFilters]);

    useEffect(() => {
        fetchContractsFiltrationArrays()
    }, [fetchContractsFiltrationArrays]);

    useEffect(() => {
        fetchContracts();
    }, [fetchContracts]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Contracts" list={true}>
            <ContractsPageHeader setOpenAddContract={setOpenAddContract} />
            <Divider />
            {loading ?
                <Box>
                    <ContractsFiltration
                        filtersEmpty={filtersEmpty}
                        contractFilters={contractFilters}
                        setContractfilters={setContractfilters}
                        totalContracts={totalContracts}
                        contractsFetched={contractsFetched}
                        filteredContractsCount={filteredContractsCount}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalContracts === 0 ?
                    <NoRecord
                        description="Vous n'avez pas encore de contrat"
                        buttonTitle="Ajouter un nouveau contrat"
                        handleOpenDialog={() => setOpenAddContract(true)}
                        hasPermission={hasContractCreatePermission}
                    /> :
                    <Box>
                        <ContractsFiltration
                            filtersEmpty={filtersEmpty}
                            contractFilters={contractFilters}
                            setContractfilters={setContractfilters}
                            totalContracts={totalContracts}
                            contractsFetched={contractsFetched}
                            filteredContractsCount={filteredContractsCount}
                        />
                        <ContractsListView
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            scrollRef={scrollRef}
                            contracts={contracts}
                            setOffset={setOffset}
                            companies={companies}
                            contractFilters={contractFilters}
                            setContractFilters={setContractfilters}
                            setContractsFetched={setContractsFetched}
                            filteredContractsCount={filteredContractsCount}
                        />
                    </Box>
            }

            {openAddContract &&
                <AddContractDialoge
                    contracts={contracts}
                    setContracts={setContracts}
                    totalContracts={totalContracts}
                    setTotalContracts={setTotalContracts}
                    filteredContractsCount={filteredContractsCount}
                    setFilteredContractsCount={setFilteredContractsCount}
                    openAddContract={openAddContract}
                    setOpenAddContract={setOpenAddContract}
                    setOpenSuccessfulAddContract={setOpenSuccessfulAddContract}
                    setContractId={setContractId}
                />
            }

            {openSuccessfulAddContract &&
                <SuccessfulAddContractDialog
                    openSuccessfulAddContract={openSuccessfulAddContract}
                    setOpenSuccessfulAddContract={setOpenSuccessfulAddContract}
                    contractId={contractId}
                    setOpenAddContract={setOpenAddContract}
                />
            }
        </Page>
        </ListNavigationProvider>
    );
};

export default Contracts;